import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import CreditsAPI from '../../api/CreditsAPI';
import InsufficientCredits from '../../pages/credit-plans/InsufficientCredits';
import {
  AceEntryCounter,
  countLineBreaks,
  isBareCrFile,
  splitLines,
} from './csvLines';

// MUI Icons
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

// ALL expected headers for the template download
const EXPECTED_HEADERS = [
  'ID',
  'Entry Summary Number',
  'Entry Summary Line Number',
  'Entry Summary and Line Number',
  'Entry Date',
  'Entry Summary Date',
  'Initial Entry Summary Create Date',
  'Entry Summary Update Date',
  'Entry Summary Reject Date',
  'Entry Type',
  'Entry Type Code',
  'Entry Type Short Description',
  'Filer Code',
  'Filer Name',
  'Entry Summary Version Number',
  'Importer Number',
  'Importer Name',
  'Designated Notify Party CF4811 Number',
  'Designated Notify Party CF4811 Name',
  'Port of Entry Code',
  'Actual Port of Entry Name',
  'Mode of Transportation Code',
  'Related Party Indicator',
  'Country of Origin Code',
  'Country of Origin Name',
  'Country of Export Code',
  'Country of Export Name',
  'Manufacturer ID',
  'Manufacturer Name',
  'Line SPI Code',
  'Tariff Ordinal Number',
  'HTS Number - Full',
  'HTS Short Description',
  'HTS Long Description',
  'Antidumping Case Number',
  'Antidumping Ad-Valorem Rate',
  'Antidumping Duty Amount',
  'Countervailing Case Number',
  'Countervailing Ad-Valorem Rate',
  'Countervailing Duty Amount',
  'Liquidation Date',
  'Liquidation Status',
  'Reconciliation Indicator',
  'Reconciliation Issue Code',
  'Reconciliation Issue',
  'Reconciliation (Other) Due Date',
  'Reconciliation (Other) Status',
  'Line PSC Reason (1) Code',
  'Line PSC Reason (1)',
  'Post Summary Correction (PSC) Indicator',
  'Protest Status',
  'Protest Number',
  'Drawback Claimed Indicator',
  'Control Status',
  'Collection Status',
  'Line Tariff UOM (1) Code',
  'Line Tariff Quantity (1)',
  'Line Goods Value Amount',
  'Line Tariff Goods Value Amount',
  'Line Tariff Duty Amount',
  'Line MPF Amount',
  'Line HMF Amount',
  'Total Estimated Fee Amount',
  'Total Liquidated Duty Amount',
  'Total Liquidated Fee Amount',
  'Continuous Bond Number',
  'Entry Summary Status',
];

// ONLY the headers strictly required for the engine to run its analysis
const REQUIRED_HEADERS = [
  'Entry Summary Number',
  'Entry Summary Line Number',
  'HTS Number - Full',
  'Country of Origin Code',
  'Line Tariff Goods Value Amount',
  'Line Tariff Duty Amount',
];

// CargoWise export — already 1 row per entry line. Column order matches
// CargoWise's Import Invoice Line Report; used for the downloadable template.
// Its value and duty columns are what let the analyzer check duty paid. The
// older statement export (no value or duty) still uploads; only the tariff-code
// checks run on it.
const CARGOWISE_EXPECTED_HEADERS = [
  'Broker Name',
  'Job Number',
  'Owner Reference',
  'Filer',
  'Entry No.',
  'Importer',
  'Supplier',
  'Manufacturer',
  'Import Date',
  'Entry Port',
  'Arrival Date',
  'Release Date',
  'Commercial Invoice Number',
  'Order References',
  'C/O',
  'Line No',
  'Goods Description',
  'Line Entered Value',
  'Curr',
  'SPI',
  'Tariff',
  'Duty Rate',
  'Prov/Prog. Tariff',
  'Prov/Prog. Duty Rate',
  'Prov/Prog. Duty',
  'Prov/Prog. Additional Tariff 1',
  'Prov/Prog. Additional Duty Rate 1',
  'Prov/Prog. Additional Duty 1',
  'Prov/Prog. Additional Tariff 2',
  'Prov/Prog. Additional Duty Rate 2',
  'Prov/Prog. Additional Duty 2',
  'Prov/Prog. Additional Tariff 3',
  'Prov/Prog. Additional Duty Rate 3',
  'Prov/Prog. Additional Duty 3',
  'Total Duty',
  'MPF',
  'HMF',
  'Total Payable',
];

// The base HTS ("Tariff"), country of origin ("C/O"), and entry number
// ("Entry No.") are the minimum needed to generate + compare expected tariffs.
const CARGOWISE_REQUIRED_HEADERS = ['Entry No.', 'C/O', 'Tariff'];

type SourceFormat = 'es003' | 'cargowise';

// The column each format uses to detect its header row.
const ENTRY_HEADER: Record<SourceFormat, string> = {
  es003: 'Entry Summary Number',
  cargowise: 'Entry No.',
};

const normalizeHeader = (header: string) =>
  header
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

interface RunAnalysisModalProps {
  open: boolean;
  handleClose: () => void;
  onAnalyze: (
    name: string,
    file: File,
    sourceFormat: SourceFormat,
    effectiveDate: string | null,
    excludeIeepa: boolean,
  ) => Promise<void>;
  cur_user: any;
  creditSpendMode: 'trial' | 'active';
}

const RunAnalysisModal: React.FC<RunAnalysisModalProps> = ({
  open,
  handleClose,
  onAnalyze,
  cur_user,
  creditSpendMode,
}) => {
  const [analysisName, setAnalysisName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Upload format. 'es003' = ACE ES-003 export (default, existing behavior);
  // 'cargowise' = CargoWise export (already 1 row per line).
  const [sourceFormat, setSourceFormat] = useState<SourceFormat>('es003');
  // Effective date for HTS rate lookups — CargoWise only, for lines whose export
  // has no Arrival Date / Import Date. Defaults to today; the user can change it
  // at upload.
  const [effectiveDate, setEffectiveDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  // Runs the analysis with every IEEPA rule removed from the engine, so no
  // IEEPA code is ever expected on a line. Mirrors the Tariff Tool's "hide
  // IEEPA" toggle. Off by default — the analysis includes IEEPA as it always
  // has unless the user opts out.
  const [excludeIeepa, setExcludeIeepa] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  // Exact credit cost, counted at submit time: 1 per unique Entry Summary
  // Number, which is what the analyzer bills. The analyzer re-counts on its
  // side and rejects the run before any processing if the balance is short,
  // so this is the up-front gate, not the final word.
  const [estimatedCredits, setEstimatedCredits] = useState(0);

  // Chunked CSV scanner. Streams the file in 5 MB chunks and produces:
  //   • totalLines      — line-break count across the whole file (bare CRs
  //                       count only in a bare-CR file; see csvLines), for
  //                       the 2M-row hard cap.
  //   • distinctEntries — unique Entry Summary Numbers across EVERY row, so
  //                       the count is exact whatever order the rows are in.
  //                       (It used to sample the first chunk and extrapolate,
  //                       which assumed rows grouped by entry and overshot
  //                       badly on shuffled files.)
  // A row cut by a chunk boundary is carried into the next chunk, and bytes
  // are decoded with a streaming TextDecoder so a multi-byte character split
  // across slices is not mangled.
  const countLinesAndEntries = async (
    file: File,
  ): Promise<{
    totalLines: number;
    headerFound: boolean;
    distinctEntries: number;
  }> => {
    return new Promise((resolve, reject) => {
      let totalLines = 0;
      // Line-ending style, decided by the first chunk's first line break.
      let bareCr = false;
      let endedOnCr = false;
      let carry = '';
      const counter = new AceEntryCounter(sourceFormat);
      const decoder = new TextDecoder('utf-8');
      const chunkSize = 1024 * 1024 * 5; // 5MB chunks
      let offset = 0;
      let isFirstChunk = true;
      const reader = new FileReader();

      reader.onload = (e) => {
        const isLastChunk = offset + chunkSize >= file.size;
        const text = decoder.decode(e.target?.result as ArrayBuffer, {
          stream: !isLastChunk,
        });

        if (isFirstChunk) bareCr = isBareCrFile(text);
        totalLines += countLineBreaks(text, bareCr, endedOnCr);
        endedOnCr = text.endsWith('\r');
        isFirstChunk = false;

        const lines = splitLines(carry + text, bareCr);
        // The last piece is a row still being read, unless the file ends here.
        carry = isLastChunk ? '' : lines.pop() || '';
        for (const line of lines) {
          if (line) counter.addLine(line);
        }

        offset += chunkSize;
        if (!isLastChunk) {
          readNextChunk();
        } else {
          resolve({
            totalLines: totalLines,
            headerFound: counter.headerFound,
            distinctEntries: counter.count,
          });
        }
      };

      reader.onerror = () => reject(reader.error);

      const readNextChunk = () => {
        const slice = file.slice(offset, offset + chunkSize);
        reader.readAsArrayBuffer(slice);
      };

      readNextChunk();
    });
  };

  const handleDownloadTemplate = () => {
    const headers =
      sourceFormat === 'cargowise' ? CARGOWISE_EXPECTED_HEADERS : EXPECTED_HEADERS;
    const fileName =
      sourceFormat === 'cargowise'
        ? 'CargoWise_Analyzer_Template.csv'
        : 'ACE_Analyzer_Template.csv';
    const csvContent = `${headers.map((h) => `"${h}"`).join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateCSV = async (
    file: File,
  ): Promise<{ isValid: boolean; missing: string[] }> => {
    let fileHeaders: string[] = [];

    // The sentinel column that identifies the header row, and the required
    // columns to enforce, both depend on the selected upload format.
    const sentinel = normalizeHeader(ENTRY_HEADER[sourceFormat]);
    const requiredHeaders =
      sourceFormat === 'cargowise'
        ? CARGOWISE_REQUIRED_HEADERS
        : REQUIRED_HEADERS;

    try {
      const text = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => resolve('');
        // Read a slightly larger chunk (8KB) just in case the disclaimer block is huge
        reader.readAsText(file.slice(0, 8192));
      });

      const lines = splitLines(text, isBareCrFile(text));

      // Scan the first 10 lines to find the actual header row
      for (let i = 0; i < Math.min(lines.length, 10); i++) {
        const currentLine = lines[i];
        if (!currentLine) continue;

        // Safely split CSV lines by commas, ignoring commas trapped inside quotes
        const parsedLineHeaders = currentLine
          .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
          .map((h) => normalizeHeader(h.replace(/(^"|"$)/g, '')));

        if (parsedLineHeaders.includes(sentinel)) {
          fileHeaders = parsedLineHeaders;
          break;
        }
      }
    } catch (error) {
      console.error('Error extracting headers for validation:', error);
    }

    // Compare extracted headers against the format's required list
    const missingHeaders = requiredHeaders.filter(
      (req) => !fileHeaders.includes(normalizeHeader(req)),
    );

    return { isValid: missingHeaders.length === 0, missing: missingHeaders };
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const validation = await validateCSV(file);

    if (validation.isValid) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      enqueueSnackbar(
        `Missing required columns: ${validation.missing.join(', ')}`,
        { variant: 'error', autoHideDuration: 6000 },
      );
    }
    // Depends on sourceFormat: validateCSV picks its sentinel + required
    // columns from the currently-selected format.
  }, [sourceFormat]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDrop,
    validator: (currentFile: File) => {
      if (
        currentFile.type !== 'text/csv' &&
        !currentFile.name.toLowerCase().endsWith('.csv')
      ) {
        enqueueSnackbar('Invalid file type. Please upload a CSV file.', {
          variant: 'error',
        });
        return { code: 'file-type-invalid', message: 'File must be a .csv' };
      }
      return null;
    },
    accept: ['text/csv', '.csv'],
    multiple: false,
  });

  const handleSubmit = async () => {
    if (!analysisName.trim() || !selectedFile) return;

    setIsSubmitting(true);
    try {
      // One streaming pass over the file produces both the row count (for
      // the 2M-row hard cap) AND the exact credit cost.
      const { totalLines, headerFound, distinctEntries } =
        await countLinesAndEntries(selectedFile);
      const rowCount = totalLines;

      // Enforce the 2 Million row limit
      if (rowCount > 2000000) {
        enqueueSnackbar(
          `Document has ${rowCount.toLocaleString()} rows. The maximum allowed is 2,000,000.`,
          { variant: 'error', autoHideDuration: 6000 },
        );
        setIsSubmitting(false);
        return;
      }

      // The exact cost when the header row was found; the old /2 heuristic
      // only when it was not (the analyzer then fails the file anyway).
      const creditEstimate = headerFound
        ? distinctEntries
        : Math.ceil(rowCount / 2);
      setEstimatedCredits(creditEstimate);

      // Check credits availability against the exact cost.
      const teamId = cur_user?.selected_team?.id;
      if (teamId) {
        const creditsSummaryResponse =
          creditSpendMode === 'trial'
            ? await CreditsAPI.getTrialCreditsSummary(teamId)
            : await CreditsAPI.getCreditsSummary(teamId);
        if (!creditsSummaryResponse?.success)
          throw new Error('Unable to validate available analysis credits.');

        const creditsSummary =
          creditSpendMode === 'trial' &&
          Array.isArray(creditsSummaryResponse.result)
            ? creditsSummaryResponse.result[0]
            : creditsSummaryResponse.result;
        const availableAnalysisCredits = Number(
          creditsSummary?.remaining_credits ?? 0,
        );
        if (creditEstimate > availableAnalysisCredits) {
          setShowInsufficientCredits(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Pass the selected CSV directly to the upload API. effectiveDate is only
      // meaningful for CargoWise (ES-003 carries a per-line entry date).
      await onAnalyze(
        analysisName,
        selectedFile,
        sourceFormat,
        sourceFormat === 'cargowise' ? effectiveDate : null,
        excludeIeepa,
      );

      setAnalysisName('');
      setSelectedFile(null);
      handleClose();

      enqueueSnackbar('Analysis started successfully.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to start analysis. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setAnalysisName('');
    setSelectedFile(null);
    setSourceFormat('es003');
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    handleClose();
  };

  const handleFormatChange = (fmt: SourceFormat) => {
    if (fmt === sourceFormat) return;
    setSourceFormat(fmt);
    // Clear any selected file — the required-column validation differs per
    // format, so a file that passed for one format may not for the other.
    setSelectedFile(null);
  };

  const isReadyToSubmit =
    analysisName.trim().length > 0 &&
    selectedFile !== null &&
    !isSubmitting &&
    // CargoWise requires an effective date (defaulted to today, but guard anyway).
    (sourceFormat !== 'cargowise' || effectiveDate.trim().length > 0);

  if (!open) return null;

  return (
    <div
      className='modal-overlay'
      onClick={handleResetAndClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        className='modal-cont'
        style={{
          width: '540px',
          maxWidth: '90vw',
          padding: 0,
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='modal-header-cont-temp'
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--es-soft-border)',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <div className='modal-header-cont-text'>
              <h1
                className='modal-header-cont-text-h1'
                style={{ fontSize: '20px', marginBottom: '4px' }}
              >
                Run New Analysis
              </h1>
              <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
                {sourceFormat === 'cargowise'
                  ? 'Upload a CargoWise export to analyze your entry lines.'
                  : 'Upload an ACE document to analyze your entry summaries.'}
              </p>
            </div>
            <div
              className='module-header-btn-close'
              onClick={handleResetAndClose}
              style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className='modal-body'
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Analysis Name Input */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <label
              style={{
                color: 'var(--text-primary-color)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Analysis Name
            </label>
            <input
              type='text'
              value={analysisName}
              onChange={(e) => setAnalysisName(e.target.value)}
              placeholder='e.g. Q4 2024 Entry Review'
              style={{
                background: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                padding: '12px 14px',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Upload Format selector */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <label
              style={{
                color: 'var(--text-primary-color)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Upload Format
            </label>
            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              {(
                [
                  { key: 'es003', label: 'ACE ES-003' },
                  { key: 'cargowise', label: 'CargoWise Export' },
                ] as { key: SourceFormat; label: string }[]
              ).map((opt) => {
                const active = sourceFormat === opt.key;
                return (
                  <button
                    key={opt.key}
                    type='button'
                    onClick={() => handleFormatChange(opt.key)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${
                        active ? '#4895ef' : 'var(--es-soft-border)'
                      }`,
                      background: active
                        ? 'rgba(72, 149, 239, 0.08)'
                        : 'var(--bg-colors)',
                      color: 'var(--text-primary-color)',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Effective Date — CargoWise only (fallback when a line has no date) */}
          {sourceFormat === 'cargowise' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%',
              }}
            >
              <label
                style={{
                  color: 'var(--text-primary-color)',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Effective Date
              </label>
              <input
                type='date'
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                style={{
                  background: 'var(--bg-colors)',
                  color: 'var(--text-primary-color)',
                  border: '1px solid var(--es-soft-border)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              <p
                style={{
                  margin: 0,
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                }}
              >
                Used to look up HTS tariff rates for any line without an Arrival
                Date or Import Date (defaults to today).
              </p>
            </div>
          )}

          {/* IEEPA option — applies to both upload formats */}
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              width: '100%',
              padding: '12px 14px',
              borderRadius: '8px',
              border: '1px solid var(--es-soft-border)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
            }}
          >
            <input
              type='checkbox'
              checked={excludeIeepa}
              onChange={(e) => setExcludeIeepa(e.target.checked)}
              style={{ marginTop: '2px', cursor: 'pointer' }}
            />
            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span
                style={{
                  color: 'var(--text-primary-color)',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Exclude IEEPA tariffs
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                Removes every IEEPA rule from the analysis, so no IEEPA code is
                expected on any line. You can change this and re-run later.
              </span>
            </span>
          </label>

          {/* Dropzone Area */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}
            >
              <label
                style={{
                  color: 'var(--text-primary-color)',
                  fontSize: '14px',
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                {sourceFormat === 'cargowise'
                  ? 'CargoWise Export (CSV)'
                  : 'ACE Document (CSV)'}
              </label>
              <span
                onClick={handleDownloadTemplate}
                style={{
                  color: '#4895ef',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Download
                Template
              </span>
            </div>

            {!selectedFile ? (
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${
                    isDragActive ? '#4895ef' : 'var(--es-soft-border)'
                  }`,
                  borderRadius: '8px',
                  padding: '40px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragActive
                    ? 'rgba(72, 149, 239, 0.05)'
                    : 'var(--bg-secondary)',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadOutlinedIcon
                  style={{
                    fontSize: 44,
                    color: 'var(--text-secondary)',
                    marginBottom: '12px',
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    color: 'var(--text-primary-color)',
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                >
                  <span style={{ color: '#4895ef' }}>Click to upload</span> or
                  drag and drop
                </p>
                <p
                  style={{
                    margin: '6px 0 0 0',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                  }}
                >
                  CSV files only • Max 2M rows
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  border: '1px solid var(--es-soft-border)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <InsertDriveFileOutlinedIcon
                    style={{ color: '#4895ef', fontSize: 28 }}
                  />
                  <div>
                    <p
                      style={{
                        margin: 0,
                        color: 'var(--text-primary-color)',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {selectedFile.name}
                    </p>
                    <p
                      style={{
                        margin: '2px 0 0 0',
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                      }}
                    >
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <div
                  onClick={() => setSelectedFile(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    color: '#f04438',
                  }}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </div>
              </div>
            )}
            <p
              style={{
                margin: '4px 0 0 0',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                fontStyle: 'italic',
              }}
            >
              * Documents must contian the required fields (see dropdown in the
              center of the Analyzers homepage) and contain 2M rows or less.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid var(--es-soft-border)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <button
            className='btn-outline'
            onClick={handleResetAndClose}
            disabled={isSubmitting}
            style={{ margin: 0, width: 'auto' }}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            disabled={!isReadyToSubmit}
            onClick={handleSubmit}
            style={{
              margin: 0,
              width: 'auto',
              opacity: isReadyToSubmit ? 1 : 0.5,
              cursor: isReadyToSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              'Analyze Document'
            )}
          </button>
        </div>
      </div>
      <InsufficientCredits
        teamId={cur_user?.selected_team?.id || ''}
        type='analysis'
        requiredCredits={estimatedCredits}
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        isAnalyzer={true}
        isTrialing={creditSpendMode === 'trial'}
      />
    </div>
  );
};

export default RunAnalysisModal;
